import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import {
  fundSectorBreakdownQueryOptions,
  fundSectorCountsQueryOptions,
  fundStockCountQueryOptions,
  fundSummaryQueryOptions,
  fundTrendsQueryOptions,
  popularStocksQueryOptions,
} from '@/features/mutual-funds/api/queries'
import { defaultTrendFunds } from '@/features/mutual-funds/lib/defaults'
import type { RouterContext } from '@/routes/layouts/root'
import { fundParamSchema, trendFundsParamSchema } from '@/screens/mutual-funds/search'

/** The four tabs under the tiles, in the order they are drawn. */
export const ANALYTICS_TABS = ['stock-count', 'trends', 'popular', 'sectors'] as const
export const analyticsTabSchema = z.enum(ANALYTICS_TABS)
export type AnalyticsTab = z.infer<typeof analyticsTabSchema>

/**
 * - `tab` — which of the four tabs is open; Fund stock count by default.
 * - `funds` — the trends chart's funds, 1–5 symbols. Absent means the top
 *   three by stock count (`defaultTrendFunds`), resolved from data, never
 *   written back into the URL until the member changes the selection.
 * - `fund` — the fund whose sector dialog is open on the Sector diversity tab.
 *   Absent means no dialog.
 */
export const analyticsSearchSchema = z.object({
  tab: analyticsTabSchema.default('stock-count').catch('stock-count'),
  funds: trendFundsParamSchema,
  fund: fundParamSchema,
})
export type AnalyticsSearch = z.infer<typeof analyticsSearchSchema>

/**
 * The `/mutual-funds` loader (Analytics).
 *
 * MAIN, awaited together: the summary (the three tiles) and the stock-count
 * list (the default tab's table, and the trends chart's default funds). The
 * page has no period control, so both read the backend's latest (`null`).
 *
 * SECONDARY, started only for a real navigation (not a hover `preload`): the
 * read behind whichever tab the entry URL opens, and the sector dialog's read
 * when `?fund=` is set, so a shared link lands with its tab already loading.
 * No `loaderDeps` — a tab click is a search-only change and runs none of this;
 * each tab's own hook reads for itself.
 */
export async function loadAnalytics({
  context: { queryClient },
  location,
  preload,
}: {
  context: RouterContext
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  const search = analyticsSearchSchema.parse(location.search)

  if (!preload) {
    if (search.tab === 'popular') prefetchRouteData(queryClient, popularStocksQueryOptions(null))
    if (search.tab === 'sectors') prefetchRouteData(queryClient, fundSectorCountsQueryOptions(null))
    if (search.fund !== undefined) {
      prefetchRouteData(queryClient, fundSectorBreakdownQueryOptions(search.fund, null))
    }
    if (search.tab === 'trends' && search.funds !== undefined && search.funds.length > 0) {
      prefetchRouteData(queryClient, fundTrendsQueryOptions({ funds: search.funds }))
    }
  }

  const [, stockCount] = await Promise.all([
    ensureRouteData(queryClient, fundSummaryQueryOptions(null)),
    ensureRouteData(queryClient, fundStockCountQueryOptions(null)),
  ])

  // The default selection needs the stock-count list, so it can only start
  // once that has landed.
  if (!preload && search.tab === 'trends' && search.funds === undefined) {
    const funds = defaultTrendFunds(stockCount)
    if (funds.length > 0) prefetchRouteData(queryClient, fundTrendsQueryOptions({ funds }))
  }
}
